/**
 * Product Detail Tab Component
 *
 * Displays detailed information about a selected product in the sidebar.
 */

import { Crosshair, Dna, ExternalLink } from 'lucide-react-native';
import { Linking, View } from 'react-native';

import { ProductLogo } from './ProductLogo';

import { DisclaimerText } from '@/components/MedicalOnDemand/DisclaimerText';
import { LI, UL } from '@/components/list';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Button, ButtonIcon, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import {
  getSupportTeamByProductId,
  type IndicationParagraph,
  type Product,
  type ProductId,
} from '@/constants/medicalOnDemand';

function IndicationParagraphBlock({
  paragraph,
  index,
  textClassName,
}: {
  paragraph: IndicationParagraph;
  index: number;
  textClassName: string;
}) {
  if (typeof paragraph === 'string') {
    return (
      <Text key={index} className={textClassName}>
        {paragraph}
      </Text>
    );
  }

  return (
    <VStack key={index} className="gap-1">
      <Text className={textClassName}>{paragraph.intro}</Text>
      <UL>
        {paragraph.bullets.map((bullet, bulletIndex) => (
          <LI key={bulletIndex}>{bullet}</LI>
        ))}
      </UL>
    </VStack>
  );
}

type ProductDetailTabProps = {
  product: Product;
  variant?: 'full' | 'stacked';
  showStackedHeading?: boolean;
};

export function ProductDetailTab({
  product,
  variant = 'full',
  showStackedHeading = true,
}: ProductDetailTabProps) {
  const supportTeam = getSupportTeamByProductId(product.id);

  const handleOpenWebsite = () => {
    Linking.openURL(product.websiteUrl).catch(() => {});
  };

  if (variant === 'stacked') {
    return (
      <VStack className="gap-4">
        {product.indicationAndUsage?.length ? (
          <VStack className="gap-4">
            {showStackedHeading ? (
              <Text className="font-bold text-base text-primary-500">
                {`${product.indicationLabel} and Usage`}
              </Text>
            ) : null}
            {product.indicationAndUsage.map((paragraph, index) => (
              <IndicationParagraphBlock
                key={index}
                paragraph={paragraph}
                index={index}
                textClassName="text-sm leading-[1.4] text-typography-900"
              />
            ))}
          </VStack>
        ) : (
          <VStack className="gap-2">
            {showStackedHeading ? (
              <Text className="font-bold text-base text-primary-500">
                {`${product.indicationLabel} and Usage`}
              </Text>
            ) : null}
            <Text className="text-sm leading-[1.4] text-typography-900">{product.indication}</Text>
          </VStack>
        )}
      </VStack>
    );
  }

  return (
    <VStack className="gap-5">
      <HStack className="items-center gap-4">
        <View className="h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-outline-200 bg-white">
          <ProductLogo
            productId={product.id as ProductId}
            size={48}
            accessibilityLabel={`${product.brandName} logo`}
          />
        </View>

        <VStack className="flex-1 gap-1">
          <Heading size="lg" className="text-typography-900">
            {product.brandName}
          </Heading>
          <Text className="text-sm text-typography-500">{product.genericName}</Text>
        </VStack>
      </HStack>

      <HStack className="flex-wrap gap-2">
        {product.drugTarget && (
          <Badge
            size="md"
            className="gap-1"
            style={{ backgroundColor: `${product.branding.primaryColor}1A` }}>
            <Icon as={Crosshair} size="xs" style={{ color: product.branding.primaryColor }} />
            <BadgeText className="text-xs" style={{ color: product.branding.primaryColor }}>
              {product.drugTarget}
            </BadgeText>
          </Badge>
        )}
        {product.drugClass && (
          <Badge
            size="md"
            className="gap-1"
            style={{ backgroundColor: `${product.branding.secondaryColor}1A` }}>
            <Icon as={Dna} size="xs" style={{ color: product.branding.secondaryColor }} />
            <BadgeText className="text-xs" style={{ color: product.branding.secondaryColor }}>
              {product.drugClass}
            </BadgeText>
          </Badge>
        )}
      </HStack>

      <Divider />

      <VStack className="gap-2">
        <Text className="font-semibold text-xs uppercase tracking-wide text-typography-500">
          {product.indicationLabel}
        </Text>
        <Text className="text-sm leading-relaxed text-typography-700">{product.indication}</Text>
      </VStack>

      {product.indicationAndUsage?.length ? (
        <>
          <Divider />

          <VStack className="gap-2">
            <Text className="font-semibold text-xs uppercase tracking-wide text-typography-500">
              {`${product.indicationLabel} And Usage`}
            </Text>
            {product.indicationAndUsage.map((paragraph, index) => (
              <IndicationParagraphBlock
                key={index}
                paragraph={paragraph}
                index={index}
                textClassName="text-sm leading-[1.4] text-typography-900"
              />
            ))}
          </VStack>
        </>
      ) : null}

      <Divider />

      <VStack className="gap-2">
        <Text className="font-semibold text-xs uppercase tracking-wide text-typography-500">
          Prescribing Information
        </Text>
        <DisclaimerText>{product.disclaimer}</DisclaimerText>
        <LinkText
          size="xs"
          className="font-medium text-primary-500 underline"
          onPress={() => {
            Linking.openURL(product.prescribingInfoUrl).catch(() => {});
          }}>
          View full Prescribing Information
        </LinkText>
      </VStack>

      <Divider />

      <VStack className="gap-2">
        <Text className="font-semibold text-xs uppercase tracking-wide text-typography-500">
          Supported By
        </Text>
        <Text className="text-sm text-typography-700">{supportTeam?.name ?? 'Unknown'}</Text>
      </VStack>

      <Divider />

      <Button
        variant="outline"
        action="secondary"
        size="sm"
        onPress={handleOpenWebsite}
        className="w-full justify-between">
        <ButtonText>Visit {product.brandName} Website</ButtonText>
        <ButtonIcon as={ExternalLink} className="ml-2" />
      </Button>
    </VStack>
  );
}
